import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  Alert,
  Chip,
  Divider,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NotesIcon from '@mui/icons-material/Notes';
import {
  fetchTestResultDetailAsync,
  selectSelectedResult,
  selectDetailLoading,
  selectDetailError,
  clearSelectedResult,
  updateTestNotesAsync,
  updateValidationStateAsync,
} from '../../redux/testHistory/testHistorySlice';
import {
  updateOutput,
  setLoading,
  setRequestStatus,
  setIncludeNonValidated,
  setIncludeOutboundBeacons,
  outputIncludeNonValidated,
  outputIncludeOutboundBeacons,
} from '../../redux/validation/validationResultsSlice';
import OutputHandler from '../validation/OutputHandler';
import TextField from '@mui/material/TextField';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import AIAnalysisDialog from './aiAnalysis/AIAnalysisDialog';
import FlowAnalysisFallback from './aiAnalysis/FlowAnalysisFallback';
import FlowSummaryReport from './aiAnalysis/FlowSummaryReport';
import { findReportByKeys, FLOW_REPORT_KEYS } from './aiAnalysis/aiReportUtils';
import useAIAnalysis, { AI_ANALYSIS_TYPES } from './aiAnalysis/useAIAnalysis';
import TestHistoryLoadingState from './list/TestHistoryLoadingState';

/**
 * TestHistoryDetail - Displays detailed view of a saved test result
 * Loads validation state and renders it using existing validation components
 */
export default function TestHistoryDetail() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const result = useSelector(selectSelectedResult);
  const loading = useSelector(selectDetailLoading);
  const error = useSelector(selectDetailError);
  const includeNonValidated = useSelector(outputIncludeNonValidated);
  const includeOutboundBeacons = useSelector(outputIncludeOutboundBeacons);

  const [validationStateLoaded, setValidationStateLoaded] = useState(false);
  const [dataDispatchedToRedux, setDataDispatchedToRedux] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const location = useLocation();

  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editedNotes, setEditedNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const {
    open: aiOpen,
    setOpen: setAiOpen,
    loading: aiLoading,
    data: aiData,
    error: aiError,
    runAnalysis,
  } = useAIAnalysis(AI_ANALYSIS_TYPES.FLOW, result?.id);
  const flowReport = findReportByKeys(aiData, FLOW_REPORT_KEYS);

  const [editableValidationState, setEditableValidationState] = useState([]);
  const [savingValidationState, setSavingValidationState] = useState(false);
  const [saveSuccessOpen, setSaveSuccessOpen] = useState(false);
  // Load test result detail when component mounts
  useEffect(() => {
    if (id) {
      console.log(`Loading test result detail for ID: ${id}`);
      dispatch(fetchTestResultDetailAsync(parseInt(id)));
    }

    // Cleanup when component unmounts
    return () => {
      dispatch(clearSelectedResult());
      setValidationStateLoaded(false);
      setDataDispatchedToRedux(false);
    };
  }, [id, dispatch]);

  // Once result is loaded, update the validation state in Redux
  useEffect(() => {
    if (result && result.validation_state && !dataDispatchedToRedux) {
      console.log('Loading validation state into Redux:', result.validation_state);

      // Check if validation_state.output exists and is an array
      if (!result.validation_state.output || !Array.isArray(result.validation_state.output)) {
        console.error(
          'validation_state.output is missing or not an array:',
          result.validation_state,
        );
        return;
      }

      // Set status to 200 (success) for historical data
      dispatch(setRequestStatus(200));
      // Explicitly set loading to false and update output
      dispatch(setLoading(false));
      dispatch(updateOutput(result.validation_state.output));
      setEditableValidationState(result.validation_state.output);
      setDataDispatchedToRedux(true);
      // Don't set validationStateLoaded here - wait for OutputHandler to be ready
    }
  }, [result, dataDispatchedToRedux, dispatch]);

  // Callback for when OutputHandler accordions are ready
  const handleOutputReady = () => {
    setValidationStateLoaded(true);
  };

  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      navigate('/test-history');
    }
  };

  // Handle notes modal
  const handleOpenNotes = () => {
    setEditedNotes(result.notes || '');
    setIsEditingNotes(false);
    setNotesOpen(true);
  };

  const handleCloseNotes = () => {
    setNotesOpen(false);
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);

    await dispatch(updateTestNotesAsync(result.id, editedNotes));

    setSavingNotes(false);
    setIsEditingNotes(false);
  };

  // Handle filter toggle changes
  const handleIncludeNonValidatedChange = (event) => {
    dispatch(setIncludeNonValidated(event.target.checked));
  };

  const handleIncludeOutboundBeaconsChange = (event) => {
    dispatch(setIncludeOutboundBeacons(event.target.checked));
  };

  const handleValidationStatusChange = (index, newStatus) => {
    setEditableValidationState((prev) =>
      prev.map((item) => {
        if (item.index !== index) {
          return item;
        }

        return {
          ...item,
          manualBeaconStatus: newStatus,
        };
      }),
    );
  };

  const getBeaconStatus = (event) => {
    // Explicit manual override from user
    if (event.manualBeaconStatus) {
      return event.manualBeaconStatus;
    }

    // Saved validation status fallback
    if (event.sequentialValidation) {
      return event.sequentialValidation;
    }

    // Default missing/unvalidated state
    if (event.isMissing || event.beaconData?.status === 404 || event.truth?.status === 404) {
      return 'NOT_VALIDATED';
    }

    return null;
  };

  const calculateSummary = (events) => {
    const summary = {
      passed: 0,
      failed: 0,
      missing: 0,
      non_validated: 0,
      not_present_in_app: 0,
    };

    events.forEach((event) => {
      const status = getBeaconStatus(event);

      switch (status) {
        case 'PASS':
          summary.passed++;
          break;

        case 'FAIL':
          summary.failed++;
          break;

        case 'MISSING':
          summary.missing++;
          break;

        case 'NOT_VALIDATED':
          summary.non_validated++;
          break;

        case 'NOT_PRESENT_IN_APP':
          summary.not_present_in_app++;
          break;

        default:
          break;
      }
    });

    const total =
      summary.passed +
      summary.failed +
      summary.missing +
      summary.non_validated +
      summary.not_present_in_app;

    return {
      total_events: total,
      ...summary,
      order_issues: 0,
      pass_rate: total > 0 ? ((summary.passed / total) * 100).toFixed(2) : '0.00',
    };
  };

  const handleSaveValidationState = async () => {
    try {
      setSavingValidationState(true);

      const summary = calculateSummary(editableValidationState);

      await dispatch(updateValidationStateAsync(result.id, editableValidationState, summary));

      setSaveSuccessOpen(true);
    } catch (error) {
      console.error(error);
    } finally {
      setSavingValidationState(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Format timestamp range
  const formatTimeRange = (startMs, endMs) => {
    const startDate = new Date(startMs);
    const endDate = new Date(endMs);
    const dateStr = startDate.toLocaleDateString();
    const startTime = startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const endTime = endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return `${dateStr} ${startTime} - ${endTime}`;
  };

  // Get device ID label based on device type
  const getDeviceIdLabel = (deviceType) => {
    if (!deviceType) return 'Device ID';
    const type = deviceType.toLowerCase();
    if (type === 'ios') return 'IDFA / IDFV';
    if (type === 'android') return 'GAID';
    return 'Device ID';
  };

  // Error state
  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{
            mb: 2,
            color: 'primary.main',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          Back to Search
        </Button>
        <Alert severity="error">
          <strong>Error loading test result:</strong> {error}
        </Alert>
      </Container>
    );
  }

  const handleAIAnalysis = () => {
    if (!result?.id) return;

    runAnalysis();
  };

  // No result loaded (only show this if not loading and no result)
  if (!result && !loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{
            mb: 2,
            color: 'primary.main',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          Back to Search
        </Button>
        <Alert severity="info">No test result found with ID: {id}</Alert>
      </Container>
    );
  }

  // Show loading spinner until result is loaded and validation state is ready
  const showSpinner = !result || !validationStateLoaded;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Loading Spinner */}
      {showSpinner && <TestHistoryLoadingState />}

      {/* Content - only render when result exists, hide visually while spinner is showing */}
      {result && (
        <Box
          sx={{
            visibility: showSpinner ? 'hidden' : 'visible',
            position: showSpinner ? 'absolute' : 'static',
            top: showSpinner ? '-9999px' : 'auto',
          }}
        >
          {/* Navigation and Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{
                color: 'primary.main',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              Back to Search
            </Button>

            {/* View Notes Button */}
            {result.notes && result.notes.trim() !== '' && (
              <Button
                startIcon={<NotesIcon />}
                onClick={handleOpenNotes}
                sx={{
                  color: 'primary.main',
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                View Notes
              </Button>
            )}
          </Box>

          {/* Test Result Header */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 2 }}>
              {result.flow_name}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Chip label={`Regression Test: ${result.regression_test_id}`} color="primary" />
              <Chip label={result.market} variant="outlined" />
              <Chip label={result.device_name} variant="outlined" />
              <Chip
                label={`${result.pass_rate.toFixed(1)}% Pass Rate`}
                color={
                  result.pass_rate === 100
                    ? 'success'
                    : result.pass_rate >= 80
                      ? 'warning'
                      : 'error'
                }
              />
              <Button
                variant="contained"
                onClick={handleAIAnalysis}
                disabled={aiLoading}
                sx={{
                  ml: 'auto',
                  backgroundColor: 'success.main',
                  color: 'success.contrastText',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'success.dark',
                  },
                }}
              >
                {aiLoading ? 'Analyzing...' : 'AI Analysis'}
              </Button>
            </Box>

            {/* Event Statistics - Compact Inline */}
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                bgcolor: 'custom.surfaceAlt',
                borderRadius: 1,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                alignItems: 'center',
              }}
            >
              <Typography variant="body2" fontWeight="bold">
                {result.total_events} Total Events
              </Typography>
              <Typography variant="body2" color="text.secondary">
                •
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="success.main">
                {result.passed_events} Passed
              </Typography>
              <Typography variant="body2" color="text.secondary">
                •
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="error.main">
                {result.failed_events} Failed
              </Typography>
              <Typography variant="body2" color="text.secondary">
                •
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="warning.main">
                {result.missing_events} Missing
              </Typography>
              <Typography variant="body2" color="text.secondary">
                •
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="warning.main">
                {result.not_present_in_app} Not Present
              </Typography>
              <Typography variant="body2" color="text.secondary">
                •
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {result.order_issues} Order Issues
              </Typography>
              <Typography variant="body2" color="text.secondary">
                •
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="text.secondary">
                {result.non_validated_events ?? 0} Non-Validated
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Test Metadata */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: 2,
                mb: 3,
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Test Case
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {result.test_case_name}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Validated On
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {formatDate(result.created_at)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  User
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {result.user_email}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Event Time Range
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {formatTimeRange(result.timestamp_start, result.timestamp_end)}
                </Typography>
              </Box>

              <Box sx={{ gridColumn: { xs: '1', sm: 'span 2', md: 'span 2' } }}>
                <Typography variant="caption" color="text.secondary">
                  {getDeviceIdLabel(result.device_type)}
                </Typography>
                <Typography variant="body1" fontWeight="bold" sx={{ wordBreak: 'break-all' }}>
                  {result.device_id || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Filter Controls */}
          {dataDispatchedToRedux && result.validation_state && (
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.paper', boxShadow: 2, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Display Filters
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={includeNonValidated}
                      onChange={handleIncludeNonValidatedChange}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: 'secondary.main',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: 'secondary.main',
                        },
                      }}
                    />
                  }
                  label="Include Non-Validated Events"
                  sx={{ fontWeight: '500' }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={includeOutboundBeacons}
                      onChange={handleIncludeOutboundBeaconsChange}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: 'secondary.main',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: 'secondary.main',
                        },
                      }}
                    />
                  }
                  label="Include Outbound Events"
                  sx={{ fontWeight: '500' }}
                />
              </Box>
            </Paper>
          )}

          {/* Validation Results - Using existing OutputHandler component */}
          {dataDispatchedToRedux && result.validation_state && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  Validation Results
                </Typography>

                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveValidationState}
                  disabled={savingValidationState}
                  sx={{
                    fontWeight: 'bold',
                    textTransform: 'none',
                  }}
                >
                  {savingValidationState ? 'Saving...' : 'Save Beacon Status'}
                </Button>
              </Box>

              <OutputHandler
                historyMode={true}
                onContentReady={handleOutputReady}
                editableValidationState={editableValidationState}
                onValidationStatusChange={handleValidationStatusChange}
              />
            </Box>
          )}

          {/* Notes Dialog */}
          <Dialog open={notesOpen} onClose={handleCloseNotes} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              Test Result Notes
            </DialogTitle>
            <DialogContent>
              {isEditingNotes ? (
                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  value={editedNotes}
                  onChange={(e) => setEditedNotes(e.target.value)}
                />
              ) : (
                <Typography
                  sx={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '0.95rem',
                    lineHeight: 1.6,
                    mt: 1,
                    color: 'text.primary',
                  }}
                >
                  {result.notes}
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
              {!isEditingNotes ? (
                <Button
                  startIcon={<EditIcon />}
                  onClick={() => setIsEditingNotes(true)}
                  sx={{
                    color: 'secondary.main',
                    fontWeight: 'bold',
                  }}
                >
                  Edit
                </Button>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    startIcon={<SaveIcon />}
                    variant="contained"
                    disabled={savingNotes}
                    onClick={handleSaveNotes}
                    sx={{
                      backgroundColor: 'secondary.main',
                      fontWeight: 'bold',
                      '&:hover': {
                        backgroundColor: 'secondary.dark',
                      },
                    }}
                  >
                    {savingNotes ? 'Saving...' : 'Save'}
                  </Button>

                  <Button
                    variant="text"
                    onClick={() => {
                      setEditedNotes(result.notes || '');
                      setIsEditingNotes(false);
                    }}
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 'bold',
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              )}

              <Button
                variant="text"
                onClick={handleCloseNotes}
                sx={{
                  color: 'text.secondary',
                  fontWeight: 'bold',
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
          <AIAnalysisDialog
            open={aiOpen}
            onClose={() => setAiOpen(false)}
            title="AI Analysis"
            titleColor="success.dark"
            loading={aiLoading}
            error={aiError}
          >
            {aiData &&
              (flowReport ? (
                <FlowSummaryReport
                  report={flowReport}
                  flowName={result.flow_name}
                  testCaseName={result.test_case_name}
                />
              ) : (
                <FlowAnalysisFallback data={aiData} />
              ))}
          </AIAnalysisDialog>
        </Box>
      )}
    </Container>
  );
}
